import React, { useState, useEffect } from 'react';
import { X, FileArrowDown, ArrowRight, CheckCircle, CircleNotch } from '@phosphor-icons/react';
import { motion, AnimatePresence } from '@/lib/motion';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Routes that must never show the exit-intent lead magnet (admin, checkout, etc.)
const SUPPRESSED_PATH_PREFIXES = ['/admin', '/checkout'];

export const ExitIntentPopup = () => {
  const location = useLocation();
  const suppressed = SUPPRESSED_PATH_PREFIXES.some((p) =>
    (location.pathname || '').toLowerCase().startsWith(p)
  );
  const [isVisible, setIsVisible] = useState(false);
  const [hasShown, setHasShown] = useState(false);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (suppressed) return;

    // Check if already shown in this session
    const alreadyShown = sessionStorage.getItem('exitPopupShown');
    if (alreadyShown) {
      setHasShown(true);
      return;
    }

    const handleMouseLeave = (e) => {
      // Only trigger when mouse leaves from top of viewport
      if (e.clientY <= 0 && !hasShown) {
        setIsVisible(true);
        setHasShown(true);
        sessionStorage.setItem('exitPopupShown', 'true');
      }
    };

    // Also trigger after 45 seconds on page
    const timer = setTimeout(() => {
      if (!hasShown) {
        setIsVisible(true);
        setHasShown(true);
        sessionStorage.setItem('exitPopupShown', 'true');
      }
    }, 45000);

    document.addEventListener('mouseleave', handleMouseLeave);
    
    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      clearTimeout(timer);
    };
  }, [hasShown, suppressed]);

  if (suppressed) return null;

  const handleClose = () => {
    setIsVisible(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/inquiries`, {
        name: 'Newsletter Signup',
        company: '-',
        email: email,
        phone: '-',
        message: 'Newsletter subscription + Finish Guide download request',
      });
      setSubmitted(true);
    } catch (err) {
      alert('Failed to submit. Please try again.');
    }
    setSubmitting(false);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-obsidian/80 backdrop-blur-sm"
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-white w-full max-w-[90vw] sm:max-w-sm shadow-2xl rounded-xl max-h-[85vh] overflow-y-auto"
            data-testid="exit-intent-popup"
          >
            {/* Mobile drag handle - hidden since we're centering now */}
            <div className="hidden w-12 h-1 bg-gray-300 rounded-full mx-auto mt-3" />
            
            {/* Close Button */}
            <button type="button"
              onClick={handleClose}
              className="absolute top-3 right-3 sm:top-4 sm:right-4 p-2 text-concrete hover:text-obsidian transition-colors z-10"
            >
              <X weight="bold" className="w-5 h-5" />
            </button>

            {/* Content */}
            <div className="p-5 sm:p-8">
              {!submitted ? (
                <>
                  {/* Icon */}
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-bronze/10 rounded-full flex items-center justify-center mb-4 sm:mb-6">
                    <FileArrowDown weight="light" className="w-6 h-6 sm:w-7 sm:h-7 text-bronze" />
                  </div>

                  {/* Text */}
                  <h2 className="font-display text-lg sm:text-xl font-bold text-obsidian mb-2">
                    BEFORE YOU GO...
                  </h2>
                  <p className="font-body text-sm text-concrete mb-4 sm:mb-6">
                    Download our <span className="font-semibold">Complete Finish Guide</span> — 50+ architectural metal finishes with specifications.
                  </p>

                  {/* Form */}
                  <form onSubmit={handleSubmit} className="space-y-3">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      required
                      className="w-full px-4 py-2.5 sm:py-3 border border-black/10 focus:border-obsidian focus:outline-none font-body text-sm"
                    />
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full py-2.5 sm:py-3 bg-obsidian text-white text-xs font-semibold uppercase tracking-wider hover:bg-bronze hover:text-obsidian disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
                    >
                      {submitting ? (
                        <CircleNotch weight="bold" className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          GET THE FREE GUIDE <ArrowRight weight="bold" className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  </form>

                  {/* Trust Indicators */}
                  <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-black/5">
                    <div className="flex items-center justify-center gap-3 sm:gap-4 text-[9px] sm:text-[10px] text-concrete">
                      <span>✓ No spam</span>
                      <span>✓ Instant download</span>
                      <span>✓ Unsubscribe anytime</span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-4 sm:py-6">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <CheckCircle weight="fill" className="w-6 h-6 sm:w-7 sm:h-7 text-green-500" />
                  </div>
                  <h2 className="font-display text-lg sm:text-xl font-bold text-obsidian mb-2">
                    CHECK YOUR EMAIL!
                  </h2>
                  <p className="font-body text-sm text-concrete mb-4">
                    Your Finish Guide is on its way to <span className="font-medium">{email}</span>
                  </p>
                  <button type="button"
                    onClick={handleClose}
                    className="px-6 py-2 border border-black/10 text-xs font-semibold uppercase tracking-wider hover:border-obsidian transition-colors"
                  >
                    CONTINUE BROWSING
                  </button>
                </div>
              )}
            </div>

            {/* Bottom Accent */}
            <div className="h-1 bg-bronze" />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ExitIntentPopup;
